import React, { Component } from 'react'
import cx from 'classnames'
import { withRouter, Prompt } from 'react-router'
import { injectIntl } from 'react-intl'
import { Text } from '@chakra-ui/react'
import { Layout, Content, Container, Header } from 'src/components/Layout'
import { Loader } from 'src/components/Loader'
import {
  getOrCreateChatIdentity,
  getKioskDocument,
  logError,
  logMessage,
  isEmpty,
  isInStandaloneMode,
} from 'src/utils/helpers'
import { isChatCurrentlyEnabled } from 'src/utils/features'
import PushService from 'src/utils/push-service'
import chatService from 'src/utils/ChatService'
import { WidgetContext } from 'src/modules/App/WidgetContext'

import intlMessagesApp from 'src/modules/App/translations'
import { UploadDropZone } from 'src/components/Upload'
import Attachment from './Types/Attachment'
import Message from './Types/Message'
import Conversations from './Conversations'
import ChatActions from './ChatActions'
import AvatarImage from 'src/components/Avatar/AvatarImage'
import withToast from 'src/components/Toast'
import intlMessages from './translations'

import 'src/theme/animation.less'
import styles from './Chat.module.less'

const isStandalone = isInStandaloneMode()

class Chat extends Component {
  constructor(props) {
    super(props)

    this.conversationRef = null
    this.dropzoneRef = null

    this.identity = null
    this.isRenderPrevented = false

    // Careful not to update state while component is unmounted
    this.state = {
      chatReady: false,
      messages: [],
      headerHeight: 0,
      isMobileWidth: false,
      isChatLoaded: false,
      width: window.innerWidth,
    }
  }

  componentDidMount() {
    const { chat, chatOptions, isAfterHours, isChatEmpty, history } = this.props

    if (!isChatCurrentlyEnabled(chat, chatOptions, isAfterHours, isChatEmpty)) {
      history.push('/')
      return true
    }

    // Set as not unmounted, to allow updating the state
    this.isRenderPrevented = false

    // TODO: PAT-1739 Figure out how to keep Chat in background (or even better just chat listeners)
    //  probably using this.props.isKeptAliveInBackground, anything set in constructor will always reset
    //  to default value when switching pages
    //  or try to move initChat in AppLayout.jsx or somewhere else globally

    if (!isStandalone) {
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    }

    this.initChat()

    window.addEventListener('resize', this.setHeaderHeight)
  }

  componentDidUpdate(prevProps, prevState) {
    if (!!prevState.messages.length !== !!this.state.messages.length && this.state.isChatLoaded) {
      this.setHeaderHeight()
    }
  }

  componentWillUnmount() {
    try {
      // Terminate chat so it can be re-initialized after user comes back to this component
      // ChatService.shutdown()

      // Set as unmounted, to prevent updating the state
      this.isRenderPrevented = true

      if (!isStandalone) {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
      }
    } catch (e) {
      logError('Chat shutdown', e)
    }

    window.removeEventListener('resize', this.setHeaderHeight)
  }

  /**
   * Prevent page reload while chat is loading.
   *
   * @param event
   */
  handleBeforeUnload = event => {
    if (!this.state.chatReady) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  getIdentity = () => {
    if (!this.identity) {
      this.identity = getOrCreateChatIdentity(this.props.user)
    }
    return this.identity
  }

  initChat = async () => {
    const {
      intl,
      user,
      setChatData,
      isAfterHours,
      setAppActionsDisabledState,
      updateChatSessionState,
    } = this.props

    // Disable global app actions (menus, etc.)
    // setAppActionsDisabledState(true)

    // Update global state so we know globally if there are some messages in the chat
    // Disabled with PAT-3069 - hiding chat after hours, because it would hide the chat,
    // and if second call to this function was interrupted (e.g. page reload) chat would remain hidden...
    // updateChatSessionState(user, true)

    try {
      const data = chatService.initResponseChatData
      // eslint-disable-next-line no-unused-vars
      chatService.onChannelCreated(channelName => {
        if (!user) {
          // NOTE: temporary dirty hack to wait other requests to finish
          setTimeout(async () => {
            if (typeof isAfterHours !== 'undefined' && isAfterHours === false) {
              await this.sendBotMessage(
                'text',
                intl.formatMessage(intlMessages.chatBotMessageFirst),
              )
            }
            await this.sendBotMessage('text', intl.formatMessage(intlMessages.chatBotMessageSecond))
            await this.sendBotMessage('email', '')
          }, 3000)
        }
      })
      // When message is received or sent
      chatService.onMessageAdded(this.onMessageAdded)
      chatService.addChannelEventListeners(true)

      // Get latest messages
      const messages = await chatService.getMessages()

      // Set messages to UI
      const hasMessages = await this.setTwilioMessages(messages)

      // Set Chat data to redux state
      setChatData(data, user, !hasMessages)

      // Update chat indicators
      await chatService.updateUnreadStates()

      // Set chat as ready at the end
      this.updateState(
        {
          chatReady: true,
        },
        () => {
          this.setState({ isChatLoaded: true })
          this.setHeaderHeight()

          // Update global state so we know globally if there are some messages in the chat
          updateChatSessionState(user, !hasMessages)
          // Enable global app actions (menus, etc.)
          setAppActionsDisabledState(false)

          if (hasMessages) {
            setTimeout(() => {
              if (this.conversationRef && this.conversationRef.hasOwnProperty('bodyPartsRef')) {
                // const height = this.conversationRef.bodyPartsRef.clientHeight;
                // this.conversationRef.bodyPartsRef.children[0].style.height = `${height - 250}px`
                this.conversationRef.bodyPartsRef.style.opacity = 1
              }
            }, 500)
          }
        },
      )
    } catch (e) {
      logError('Chat init', e)
    }
  }

  /**
   * Central place for updating the state, because we shouldn't update it when Chat is unmounted.
   *
   * @param {mixed} state New state to set.
   * @param {function} callback Called after state is set.
   */
  updateState = (state, callback) => {
    if (this.isRenderPrevented === false) {
      this.setState(state, () => {
        if (typeof callback === 'function') {
          callback()
        }
      })
    }
  }

  /**
   * Update messages when messages are loaded or new one is sent/received.
   *
   * @param {array} messages
   * @param {function|null} callback
   */
  updateMessages = (messages, callback = null) => {
    const { user, updateChatSessionState } = this.props

    this.updateState(
      {
        messages: messages,
      },
      () => {
        setTimeout(() => {
          // Scrolls the chat down to show new messages
          if (this.conversationRef && this.conversationRef.hasOwnProperty('bodyPartsRef')) {
            this.conversationRef.bodyPartsRef.scrollTop = this.conversationRef.bodyPartsRef.scrollHeight
          }
        }, 500)

        const hasMessages = messages && messages.length > 0

        // Update global state so we know globally if there are some messages in the chat
        updateChatSessionState(user, !hasMessages)

        if (typeof callback === 'function') {
          callback()
        }
      },
    )
  }

  currentMessages = () => {
    return this.state.messages || []
  }

  setConversationRef = ref => {
    this.conversationRef = ref
  }

  setDropzoneRef = ref => {
    this.dropzoneRef = ref
  }

  setTwilioMessages = messagePage =>
    new Promise(resolve => {
      if (
        !messagePage ||
        !messagePage.hasOwnProperty('items') ||
        !messagePage.items ||
        isEmpty(messagePage.items)
      ) {
        return resolve(false)
      }

      this.updateMessages(messagePage.items, () => {
        return resolve(true)
      })
    })

  openDropzone = () => {
    this.dropzoneRef.open()
  }

  sendBotMessage = (type, text) => {
    chatService.sendBotMessage(type, [{ type, text }])
  }

  /**
   * Update Push Subscription.
   *
   * Either show "Allow" popup to save new Push Subscription or just sync existing Push Subscription.
   *
   * @return {boolean|void}
   */
  requestPushPermission = () => {
    try {
      const { featureFlags, isWidget, user } = this.props

      // Request permission to receive push notifications
      if (PushService.isEnabled('chat', user, featureFlags, isWidget)) {
        PushService.requestPermissionToAllowNotifications(isWidget)
      }
    } catch (e) {
      logError('Push Subscription request error', e)
    }
  }

  sendMessage = message => {
    const identity = this.getIdentity()

    this.updateMessages([...this.currentMessages(), new Message(message, identity)])

    this.requestPushPermission()
  }

  handleBack = () => {
    const { history } = this.props

    if (history.length - 1) {
      history.goBack()
    } else {
      history.push('/')
    }
  }

  bodyReplace = messages => {
    const { staffMembers, headerMessage, chat, intl, isChatOnlyFeature } = this.props

    const hasMessages = messages.length > 0
    const hasStaffMembers = staffMembers && staffMembers.length > 0

    const classNamesHeaderBody = cx({
      [styles.chatHeaderBody]: true,
      [styles.chatHeaderBodyWithoutMessagesWithoutAvatarsWithChatMessage]:
        !hasMessages && !hasStaffMembers && !!headerMessage,
      [styles.chatHeaderBodyWithBack]: !isChatOnlyFeature,
    })

    const classNamesHeaderAvatarsHolder = cx({
      [styles.headerAvatarsHolder]: true,
      [styles.headerAvatarsHolderWithChatMessage]: !!headerMessage,
    })

    const classNamesHeaderAvatars = cx({
      [styles.headerAvatars]: true,
      [styles.headerAvatarsWithDualStaff]:
        staffMembers && staffMembers.length > 0 && staffMembers.length % 2 === 0,
    })

    let staffMembersCount = 0

    return (
      <div className={styles.chatHeaderBodyHolder}>
        <div className={classNamesHeaderBody}>
          <Text fontWeight={'bold'} variant='contrast' fontSize={'20px'}>
            {intl.formatMessage(intlMessages.chatHeadline)}
          </Text>
          <Text
            className={styles.chatHeaderTextClamp}
            mt={'2px'}
            opacity={0.5}
            variant='contrast'
            fontSize={'14px'}
            title={chat.welcomeMessage}
          >
            {chat && chat.welcomeMessage ? chat.welcomeMessage : ''}
          </Text>
          {hasMessages && hasStaffMembers ? (
            <div className={classNamesHeaderAvatarsHolder}>
              <div className={classNamesHeaderAvatars}>
                {staffMembers.map(({ avatar }) => {
                  staffMembersCount++

                  // Show only first 3
                  if (staffMembersCount > 3) {
                    return ''
                  }

                  // Take default avatar image if not received
                  const getAvatar =
                    avatar || require('./Conversations/MessageItem/assets/admin-profile.png')

                  const isFirst = staffMembersCount === 1

                  const classNamesAvatarHolder = cx({
                    [styles.headerAvatarHolder]: true,
                    [styles.headerAvatarHolderNoMessage]: !hasMessages,
                    [styles.headerAvatarHolderFirst]: isFirst && hasMessages,
                    [styles.headerAvatarHolderNotFirst]: !isFirst && hasMessages,
                    'kiosk-color--brand-contrast-border-color': true,
                  })

                  return (
                    <div
                      key={staffMembersCount}
                      className={classNamesAvatarHolder}
                      style={{ backgroundImage: `url(${getAvatar})` }}
                    />
                  )
                })}
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    )
  }

  onDrop = files => {
    if (!files || !files.length) {
      return
    }

    const identity = this.getIdentity()

    const messages = files.map(file => {
      return new Attachment(file, identity)
    })

    this.updateMessages([...this.currentMessages(), ...messages])
  }

  onMessageAdded = message => {
    const { ...messageDetails } = message.state
    // Log sent or received message (but without body content)
    logMessage('Twilio message', messageDetails)

    const identity = this.getIdentity()

    const staffMemberData = this.props.staffMembers.find(
      staffMember =>
        staffMember.hasOwnProperty('email') && staffMember.email === message.state.author,
    )
    if (!staffMemberData) {
      this.props.updateStaffMembers(message.state.author)
    }
    // When sending a message
    if (message.state.author === identity) {
      // Reset "read" counter if sending a message
      chatService.updateLastConsumedAndUnreadStates(message)
      return
    }

    chatService.updateUnreadStates()
    this.updateMessages([...this.currentMessages(), message])
  }

  renderAvatars = avatars => {
    try {
      const { isWidgetState } = this.props
      const isSMobileWidth = document.body.clientWidth < 350

      if (!(avatars && avatars.length > 0)) {
        return ''
      }

      const messagesExists = this.currentMessages().length > 0

      const noMessageOverlay = cx({
        'kiosk-color--brand-background': true,
        [styles.noMessagesOverlay]: true,
        'cy-overlayhidden': messagesExists,
      })

      return (
        <div
          data-cy='messageOverlay'
          // eslint-disable-next-line react/no-unknown-property
          display-if={!messagesExists}
          className={noMessageOverlay}
        >
          <div className={styles.users}>
            {avatars.map(({ avatar, name }, key) => {
              return (
                <div key={key} className={styles.user + ' ' + styles['user--num-' + (key + 1)]}>
                  <AvatarImage
                    avatar={avatar}
                    size={isWidgetState || isSMobileWidth ? '60px' : '75px'}
                  />
                  <div className={styles.userName + ' kiosk-color--brand-contrast-color'}>
                    {name}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    } catch (e) {
      logError('Could not render avatars', e)
      return ''
    }
  }

  setHeaderHeight = () => {
    const { isWidgetState } = this.props
    const messagesExists = this.currentMessages().length > 0
    let headerSubElem = null
    let headerElem = document.querySelector(`.${styles.chatHeader}`)
    if (isWidgetState) {
      headerElem = getKioskDocument().querySelector(`.${styles.chatHeader}`)

      if (!messagesExists) {
        headerSubElem = getKioskDocument().querySelector("div[class*='Chat_noMessagesOverlay']")
      }
    } else {
      if (!messagesExists) {
        headerSubElem = document.querySelector("div[class*='Chat_noMessagesOverlay']")
      }
    }

    if (typeof headerElem != 'undefined' && headerElem != null) {
      const isMobileWidth = document.body.clientWidth < 900

      if (!messagesExists) {
        const subtractor = isMobileWidth || isWidgetState ? 126 : 113

        if (typeof headerSubElem != 'undefined' && headerSubElem != null) {
          this.setState({
            headerHeight: headerElem.offsetHeight + headerSubElem.offsetHeight - subtractor,
            isMobileWidth: isMobileWidth,
          })
        } else {
          this.setState({
            headerHeight: headerElem.offsetHeight,
            isMobileWidth: isMobileWidth,
          })
        }
      } else {
        this.setState({
          headerHeight: headerElem.offsetHeight,
          isMobileWidth: isMobileWidth,
        })
      }
    }
  }

  render() {
    if (!this.state.chatReady) {
      return <Loader />
    }

    const { sendingData, staffMembers, intl, isChatOnlyFeature, isWidget, user } = this.props

    const identity = this.getIdentity()

    const messages = this.currentMessages()

    const classNamesChatHeader = cx({
      'chat-header': true,
      [styles.chatHeader]: true,
      [styles.chatHeaderwithStaffList]: staffMembers && staffMembers.length,
    })

    return (
      <Layout>
        <Prompt
          when={sendingData}
          message={() => intl.formatMessage(intlMessagesApp.appGlobalNavNavigateAwayNotice)}
        />
        <Content>
          <Header
            title={intl.formatMessage(intlMessages.chatHeadline)}
            showBack={!isChatOnlyFeature}
            onBack={this.handleBack}
            className={classNamesChatHeader}
            classNameLeft={styles.chatHeaderLeft}
            classNameRight={styles.chatHeaderRight}
            bodyReplace={() => this.bodyReplace(messages)}
            showBackText={true}
          />
          <Container /* fixedTop={false} */>
            <UploadDropZone
              dropZoneRef={this.setDropzoneRef}
              disableSelectionOnClick={true}
              onDrop={this.onDrop}
              fullSpread={true}
              renderDragActiveContent={() => (
                <div className={styles.dropZoneOverlay}>
                  <p>Drop files here</p>
                </div>
              )}
              classNameDropZone={styles.dropZone}
            >
              {this.renderAvatars(staffMembers)}
              <Conversations
                ref={this.setConversationRef}
                messages={messages}
                staffMembers={staffMembers}
                isWidget={isWidget}
                isChatOnlyFeature={isChatOnlyFeature}
                identity={identity}
                resetUnreadStates={() => chatService.resetUnreadStates()}
                userEmail={user && user.hasOwnProperty('email') ? user.email : null}
                headerHeight={this.state.headerHeight}
                isMobileWidth={this.state.isMobileWidth}
              />
              <ChatActions
                enableAttachment
                enableEmoji
                isWidget={isWidget}
                isChatOnlyFeature={isChatOnlyFeature}
                placeholder={intl.formatMessage(intlMessages.chatSendAMessage)}
                onLinkClick={this.openDropzone}
                onMessageSend={this.sendMessage}
                resetUnreadStates={() => chatService.resetUnreadStates()}
              />
            </UploadDropZone>
          </Container>
          {/* <NavBottom /> */}
        </Content>
      </Layout>
    )
  }
}

const ChatContext = props => (
  <WidgetContext.Consumer>
    {context => <Chat {...props} isWidget={context.isWidget} />}
  </WidgetContext.Consumer>
)

export default withRouter(injectIntl(withToast(ChatContext)))
